import ExcelJS from "exceljs";
import {saveAs} from 'file-saver'
import React from "react";

// Formats number to appear with commas
export const numberWithCommas = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function formatFirestoreDateShort(timestamp) {
    if (!timestamp) return "";

    let date;

    // Firestore Timestamp
    if (timestamp?.toDate) {
        date = timestamp.toDate();
    }
    // Already Date
    else if (timestamp instanceof Date) {
        date = timestamp;
    }
    // Firestore raw object
    else if (timestamp?.seconds) {
        date = new Date(timestamp.seconds * 1000);
    }
    // STRING (this is probably your case)
    else if (typeof timestamp === "string") {
        date = new Date(timestamp);
    }

    if (!date || isNaN(date.getTime())) return "";

    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
}


// This Exports order data to an Excel spreadsheet
export const downloadExcel = async (ordersArray, fileName) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(fileName.replace(".xlsx", ""));
    
    // Define columns with custom widths
    worksheet.columns = [
        {header: "Code", key: "salesCode", width: 5, bold: true},
        {header: "Sales Rep", key: "salesRep", width: 20},
        {header: "Sales Rep Email", key: "salesEmail", width: 30},
        {header: "Customer Code", key: "customerCode", width: 15},
        {header: "Customer", key: "customer", width: 25},
        {header: "Order Number", key: "orderNum", width: 10},
        {header: "Status", key: "status", width: 12},
        {header: "Order Date", key: "orderDate", width: 15},
        {header: "SubTotal", key: "subTotal", width: 12},
        {header: "Discount Rate", key: "discount", width: 12},
        {header: "Discount Total", key: "discountTotal", width: 12},
        {header: "GrandTotal", key: "grandTotal", width: 12},
    ];
    
    // Add headers (make them bold)
    worksheet.addRow(worksheet.columns.map(col => col.header)).font = {bold: true};
    
    ordersArray.forEach(order => {
        // Main order row (with grey background)
        const orderRow = worksheet.addRow([
            order.salesCode, order.salesRep, order.salesEmail, order.customerCode,
            order.customerName, order.orderNum || "NA", order.status, order.orderDate,
            order.subTotal || 0, order.discount || 0, order.discountTotal || 0, order.grandTotal || 0
        ]);
        
        // ✅ Apply light grey background to the **entire** order row
        orderRow.eachCell((cell) => {
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: {argb: "D3D3D3"} // Light grey
            };
        });
        
        // Add products from subarray, keeping alignment
        if (order.products && order.products.length > 0) {
            order.products.forEach(product => {
                worksheet.addRow([
                    "", // Skip first column (Sales Code)
                    "", // Skip second column (Sales Rep)
                    product.name,
                    product.price,
                    product.discountAmount || 0,
                    product.quantity,
                    product.selectedVolume,
                    product.warehouse,
                    product.total,
                    product.reasonCode || "----",
                    product.productCode,
                ]);
            });
        }
    });
    
    // Generate the file as a Blob and trigger download
    const buffer = await workbook.xlsx.writeBuffer();
    const excelBlob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(excelBlob, fileName);
};

export const downloadExcelCustomer = async (customersArray, fileName) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(fileName.replace(".xlsx", ""));
    
    // Define columns with custom widths
    worksheet.columns = [
        {header: "Customer Code", key: "customerCode", width: 15},
        {header: "Address", key: "address1", width: 30},
        {header: "Address 2", key: "address2", width: 30},
        {header: "City", key: "city", width: 20},
        {header: "Contact", key: "contact", width: 25},
        {header: "Contact Email", key: "contactEmail", width: 30},
        {header: "Contact Phone", key: "contactPhone", width: 15},
        {header: "Country", key: "country", width: 10},
        {header: "County", key: "county", width: 10},
        {header: "Fips Code", key: "fips", width: 10},
        {header: "Credit Limit", key: "creditLimit", width: 15},
        {header: "Existing Customer", key: "existing", width: 15},
        {header: "Industry", key: "industry", width: 15},
        {header: "Customer Name", key: "name", width: 30},
        {header: "Price Type", key: "priceType", width: 10},
        {header: "Sales Code", key: "salesCode", width: 10},
        {header: "Sales Rep Email", key: "salesEmail", width: 30},
        {header: "Sales Rep", key: "salesRep", width: 20},
        {header: "State", key: "state", width: 10},
        {header: "Status", key: "status", width: 12},
        {header: "ZIP", key: "zip", width: 10},
    ];
    
    // Add headers (make them bold)
    // worksheet.addRow(worksheet.columns.map(col => col.header)).font = { bold: true };
    
    customersArray.forEach(cust => {
        // Main order row (with grey background)
        // eslint-disable-next-line no-unused-vars
        const customerRow = worksheet.addRow([
            cust.customerCode,
            cust.address1,
            cust.address2,
            cust.city,
            cust.contact,
            cust.contactEmail,
            formatPhoneNumber(cust.contactPhone),
            cust.country,
            cust.county,
            cust.fips,
            cust.creditLimit,
            cust.existing,
            cust.industry,
            cust.name,
            cust.priceType,
            cust.salesCode,
            cust.salesEmail,
            cust.salesRep,
            cust.state,
            cust.status,
            cust.zip
        ]);
        
        // ✅ Apply light grey background to the **entire** order row
        customerRow.eachCell((cell) => {
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "cfcecc" } // Light grey
            };
        });
        
        // Add products from subarray, keeping alignment
        // if (cust.ShippingAddresses && cust.ShippingAddresses.length > 0) {
        //     cust.ShippingAddresses.forEach(address => {
        //         worksheet.addRow([
        //             "", // Skip first column (Sales Code)
        //             "", // Skip second column (Sales Rep)
        //             address.address1,
        //             address.address2,
        //             address.city,
        //             address.contactName,
        //             formatPhoneNumber(address.contactPhone),
        //             address.country,
        //             address.fips,
        //             address.id,
        //             address.nickName,
        //             address.selectedCounty,
        //             address.selectedState,
        //             address.shipEmail,
        //             address.shipNotes,
        //             address.zip
        //         ]);
        //     });
        // }
    });
    
    // Generate the file as a Blob and trigger download
    const buffer = await workbook.xlsx.writeBuffer();
    const excelBlob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(excelBlob, fileName);
};


// This Formats a phone number (---) --- - ----
export const formatPhoneNumber = (phoneNumber) => {
    // Remove any non-digit characters (just in case)
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    
    // Check if the input is valid (10 digits)
    if (cleaned.length !== 10) {
        return phoneNumber; // Return the original input if invalid
    }
    
    // Format the phone number (XXX) XXX-XXXX
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    
    return phoneNumber; // Return the original input if not formatted
};


// Formats Time in just hh:mm
export const formatTime = (timestamp) => {
    if (!timestamp) return ""; // Return an empty string for null or undefined values
    // Convert the timestamp to a Date object
    const date = new Date(timestamp);
    
    // Get hours, minutes, and period (AM/PM)
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const period = hours >= 12 ? "PM" : "AM";
    
    // Convert to 12-hour format
    hours = hours % 12 || 12; // Convert 0 to 12 for midnight
    
    // Format minutes to always be 2 digits
    const formattedMinutes = minutes.toString().padStart(2, "0");
    
    // Return the formatted time string
    return `${hours}:${formattedMinutes} ${period}`;
};


// Formats an address with lineBreaks
export const formatAddressWithLineBreaks = (address) => {
    if (!address || address === "---") return "---";
    return address.split(",").map((part, index) => (
        <React.Fragment key={index}>
            {part.trim()}
            <br/>
        </React.Fragment>
    ));
};


// Formats a Time stamp in this fashion mm dd yyyy hh:mm am
// export function formatTimestamp(timestamp) {
//     const date = new Date(timestamp);
//
//     // Define month names
//     const months = [
//         "Jan", "Feb", "Mar", "Apr", "May", "Jun",
//         "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
//     ];
//
//     // Get the month abbreviation
//     const month = months[date.getMonth()];
//
//     // Get the day with ordinal suffix
//     const day = date.getDate();
//     const suffix = (day % 10 === 1 && day !== 11) ? "st" :
//         (day % 10 === 2 && day !== 12) ? "nd" :
//             (day % 10 === 3 && day !== 13) ? "rd" : "th";
//
//     // Get the full year
//     const year = date.getFullYear();
//
//     // Format time in 12-hour format
//     let hours = date.getHours();
//     const minutes = String(date.getMinutes()).padStart(2, "0");
//     const amPm = hours >= 12 ? "PM" : "AM";
//     hours = hours % 12 || 12; // Convert 24-hour time to 12-hour format
//
//     // Combine formatted date and time
//     return `${month} ${day}${suffix} ${year}, ${hours}:${minutes} ${amPm}`;
// }
export function formatTimestamp(timestamp) {
  const date = new Date(timestamp);

  const months = [
    "Jan","Feb","Mar","Apr","May","Jun",
    "Jul","Aug","Sep","Oct","Nov","Dec"
  ];

  const month = months[date.getMonth()];
  const day = date.getDate();

  const suffix =
    (day % 10 === 1 && day !== 11) ? "st" :
    (day % 10 === 2 && day !== 12) ? "nd" :
    (day % 10 === 3 && day !== 13) ? "rd" : "th";

  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const amPm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return (
    <span className="inline-flex items-baseline gap-1 text-xs font-medium">
      
      {/* Date */}
      <span>
        {month} {day}
        <span className="text-gray-500 text-xxs align-super">{suffix}</span>
      </span>

      {/* Year */}
      <span className="text-gray-500">{year}</span>

      {/* Divider */}
      <span className="text-gray-300">|</span>

      {/* Time */}
      <span className="font-semibold">
        {hours}:{minutes}
      </span>

      <span className="text-gray-500 text-xs">{amPm}</span>

    </span>
  );
}

// Formats a Time stamp in this fashion mm dd yyyy hh:mm am
export function formatTimeStampDate(timestamp) {
    const date = new Date(timestamp);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
        return false;
    }
    
    // Get the month abbreviation
    const month = date.getMonth() + 1;
    
    // Get the day with ordinal suffix
    const day = date.getDate();
    
    // Get the full year
    const year = date.getFullYear();

    // Combine formatted date and time
    return `${month}/${day}/${year}`;
}


// Function to download chart as an image
export const download = (chartRef) => {
    if (chartRef.current) {
        chartRef.current.download();
    }
};


// Function to download chart at fixed size
export const downloadFixedSize = (chartRef) => {
    if (chartRef.current) {
        chartRef.current.download({width: 600, height: 300});
    }
};


// Function to open chart as an image in new tab
export const openImage = (chartRef) => {
    if (chartRef.current) {
        chartRef.current.getImageDataURL({width: 600, height: 300}).then((imageDataURL) => {
            const image = new Image();
            image.src = imageDataURL;
            const tab = window.open();
            if (tab) {
                tab.document.write(image.outerHTML);
                tab.document.close();
            }
        });
    }
};


// Function that extracts number from a string
export const extractNumbers = (str) => {
    return str.replace(/\D/g, ''); // \D matches any non-digit character
};


// All of the UM mapping
export const UMMapping = {
    "0": "Select an U/M...",
    ACR: "Acre",
    B12: "BAG 12#",
    B22: "BAG 22#",
    B25: "BAG 25#",
    B30: "BAG 30#",
    B40: "BAG 40#",
    B5: "BAG 5#",
    B50: "BAG 50#",
    B51: "BAG 51#",
    BAG: "BAG (Unspecified Weight)",
    "2.5": "CASE (2x2.5)",
    CS4: "CASE (4X1)",
    CAS: "CASE",
    "30": "DRUM 30-GAL",
    "55": "DRUM 55-GAL",
    DRU: "DRUM (Unspecified Volume)",
    EAC: "EACH",
    GAL: "GALLON",
    TR: "TOTE REFILL",
    LB: "POUND",
    OZ: "Ounce - Av",
    PAI: "PAIL 5-GAL",
    SAC: "Super Sack (B1000)",
    SAC2: "Super Sack (B2000)",
    TON: "TON (2,000#)",
    "275": "TOTE 275-GAL",
    
    "0.25ACR": "1/4 Ton Per Acre",
    "0.5ACR": "1/2 Ton Per Acre",
    "0.75ACR": "3/4 Ton Per Acre",
    "1ACR": "1 Ton Per Acre",
    "1.25ACR": "1 1/4 Ton Per Acre",
    "1.5ACR": "1 1/2 Ton Per Acre",
    "2ACR": "2 Ton Per Acre"
    
};


// Category Colors
export const categoryColors = [
    {category: 'Biostimulants', color: 'bg-bsColor', shortened: 'BS'},
    {category: 'Liquid Fertilizer', color: 'bg-lfColor', shortened: 'LF'},
    {category: 'Dry Fertilizer', color: 'bg-teal-700', shortened: 'DF'},
    {category: 'Secondary Nutrients', color: 'bg-snColor', shortened: 'SN'},
    {category: 'Micronutrients', color: 'bg-mnColor', shortened: 'MN'},
    {category: 'Penetrants & Water Holding Agents', color: 'bg-whColor', shortened: 'PHA'},
    {category: 'A-Plus® Solubilizing Surfactants', color: 'bg-apColor', shortened: 'AP'},
    {category: 'Turfgrass Pigment', color: 'bg-tpColor', shortened: 'TP'},
    {category: 'Water Treatments', color: 'bg-wtColor', shortened: 'WT'},
    {category: 'Soil Amendments', color: 'bg-saColor', shortened: 'SA'},
    {category: 'Fungicides', color: 'bg-fgColor', shortened: 'PS'},
    {category: 'Equipment', color: 'bg-gray-600', shortened: 'EQ'},
    {category: 'Test', color: 'bg-emerald-500', shortened: 'TE'},
    {category: 'Seed', color: 'bg-green-600', shortened: 'SE'},
    {category: 'Promotional Item', color: 'bg-blue-600', shortened: 'PR'},
];


// Function to get colors
export const getCategoryInfo = (category) => {
    const categoryInfo = categoryColors.find(c => c.category === category);
    return categoryInfo ? {color: categoryInfo.color, shortened: categoryInfo.shortened} : {color: '', shortened: ''};
};


// Pricing Levels
export const pricingMapping = {
    AG: "Agriculture",
    DIST1: "Distributor NE/INTL",
    DIST2: "Distributor SE/CAN",
    NE: "North East",
    SE: "South East"
};

// Pricing Levels
export const regionMapping = {
    AG: "Agriculture",
    DIST1: "Distributor NE/INTL",
    DIST2: "Distributor SE/CAN",
    NE: "North East",
    SE: "South East",
    COM: "South East & International",
};


// Supervisor Levels
export const supervisorMapping = {
    GP: "Grant Platz",
    JN: "Jordan Nemitz",
    TW: "Tom Weinert",
    TP: "Ted Platz",
    
}


// Account Levels
export const accountLevelMapping = {
    D: "Developer",
    A: "Admin",
    CSR: "CSR",
    PFC: "PFC Fulfillment",
    DIST: "Distributor",
    S: "Sales Rep",
    M: "Marketing"
}


// State code mapping
export const stateMapping = {
    AL: "Alabama",
    AK: "Alaska",
    AZ: "Arizona",
    AR: "Arkansas",
    CA: "California",
    CO: "Colorado",
    CT: "Connecticut",
    DE: "Delaware",
    FL: "Florida",
    GA: "Georgia",
    HI: "Hawaii",
    ID: "Idaho",
    IL: "Illinois",
    IN: "Indiana",
    IA: "Iowa",
    KS: "Kansas",
    KY: "Kentucky",
    LA: "Louisiana",
    ME: "Maine",
    MD: "Maryland",
    MA: "Massachusetts",
    MI: "Michigan",
    MN: "Minnesota",
    MS: "Mississippi",
    MO: "Missouri",
    MT: "Montana",
    NE: "Nebraska",
    NV: "Nevada",
    NH: "New Hampshire",
    NJ: "New Jersey",
    NM: "New Mexico",
    NY: "New York",
    NC: "North Carolina",
    ND: "North Dakota",
    OH: "Ohio",
    OK: "Oklahoma",
    OR: "Oregon",
    PA: "Pennsylvania",
    RI: "Rhode Island",
    SC: "South Carolina",
    SD: "South Dakota",
    TN: "Tennessee",
    TX: "Texas",
    UT: "Utah",
    VT: "Vermont",
    VA: "Virginia",
    WA: "Washington",
    WV: "West Virginia",
    WI: "Wisconsin",
    WY: "Wyoming",
    DC: "Washington DC (District of Columbia)"
};


export const countryMapping = {
    "United States": "United States",
    "Afghanistan": "Afghanistan",
    "Albania": "Albania",
    "Algeria": "Algeria",
    "Andorra": "Andorra",
    "Angola": "Angola",
    "Antigua and Barbuda": "Antigua and Barbuda",
    "Argentina": "Argentina",
    "Armenia": "Armenia",
    "Australia": "Australia",
    "Austria": "Austria",
    "Azerbaijan": "Azerbaijan",
    "Bahamas": "Bahamas",
    "Bahrain": "Bahrain",
    "Bangladesh": "Bangladesh",
    "Barbados": "Barbados",
    "Belarus": "Belarus",
    "Belgium": "Belgium",
    "Belize": "Belize",
    "Benin": "Benin",
    "Bhutan": "Bhutan",
    "Bolivia": "Bolivia",
    "Bosnia and Herzegovina": "Bosnia and Herzegovina",
    "Botswana": "Botswana",
    "Brazil": "Brazil",
    "Brunei": "Brunei",
    "Bulgaria": "Bulgaria",
    "Burkina Faso": "Burkina Faso",
    "Burundi": "Burundi",
    "Cabo Verde": "Cabo Verde",
    "Cambodia": "Cambodia",
    "Cameroon": "Cameroon",
    "Canada": "Canada",
    "Central African Republic": "Central African Republic",
    "Chad": "Chad",
    "Chile": "Chile",
    "China": "China",
    "Colombia": "Colombia",
    "Comoros": "Comoros",
    "Congo (Congo-Brazzaville)": "Congo (Congo-Brazzaville)",
    "Congo (Democratic Republic)": "Congo (Democratic Republic)",
    "Costa Rica": "Costa Rica",
    "Croatia": "Croatia",
    "Cuba": "Cuba",
    "Cyprus": "Cyprus",
    "Czechia": "Czechia",
    "Denmark": "Denmark",
    "Djibouti": "Djibouti",
    "Dominica": "Dominica",
    "Dominican Republic": "Dominican Republic",
    "Ecuador": "Ecuador",
    "Egypt": "Egypt",
    "El Salvador": "El Salvador",
    "Equatorial Guinea": "Equatorial Guinea",
    "Eritrea": "Eritrea",
    "Estonia": "Estonia",
    "Eswatini": "Eswatini",
    "Ethiopia": "Ethiopia",
    "Fiji": "Fiji",
    "Finland": "Finland",
    "France": "France",
    "Gabon": "Gabon",
    "Gambia": "Gambia",
    "Georgia": "Georgia",
    "Germany": "Germany",
    "Ghana": "Ghana",
    "Greece": "Greece",
    "Grenada": "Grenada",
    "Guatemala": "Guatemala",
    "Guinea": "Guinea",
    "Guinea-Bissau": "Guinea-Bissau",
    "Guyana": "Guyana",
    "Haiti": "Haiti",
    "Honduras": "Honduras",
    "Hungary": "Hungary",
    "Iceland": "Iceland",
    "India": "India",
    "Indonesia": "Indonesia",
    "Iran": "Iran",
    "Iraq": "Iraq",
    "Ireland": "Ireland",
    "Israel": "Israel",
    "Italy": "Italy",
    "Jamaica": "Jamaica",
    "Japan": "Japan",
    "Jordan": "Jordan",
    "Kazakhstan": "Kazakhstan",
    "Kenya": "Kenya",
    "Kiribati": "Kiribati",
    "Kuwait": "Kuwait",
    "Kyrgyzstan": "Kyrgyzstan",
    "Laos": "Laos",
    "Latvia": "Latvia",
    "Lebanon": "Lebanon",
    "Lesotho": "Lesotho",
    "Liberia": "Liberia",
    "Libya": "Libya",
    "Liechtenstein": "Liechtenstein",
    "Lithuania": "Lithuania",
    "Luxembourg": "Luxembourg",
    "Madagascar": "Madagascar",
    "Malawi": "Malawi",
    "Malaysia": "Malaysia",
    "Maldives": "Maldives",
    "Mali": "Mali",
    "Malta": "Malta",
    "Marshall Islands": "Marshall Islands",
    "Mauritania": "Mauritania",
    "Mauritius": "Mauritius",
    "Mexico": "Mexico",
    "Micronesia": "Micronesia",
    "Moldova": "Moldova",
    "Monaco": "Monaco",
    "Mongolia": "Mongolia",
    "Montenegro": "Montenegro",
    "Morocco": "Morocco",
    "Mozambique": "Mozambique",
    "Myanmar": "Myanmar",
    "Namibia": "Namibia",
    "Nauru": "Nauru",
    "Nepal": "Nepal",
    "Netherlands": "Netherlands",
    "New Zealand": "New Zealand",
    "Nicaragua": "Nicaragua",
    "Niger": "Niger",
    "Nigeria": "Nigeria",
    "North Korea": "North Korea",
    "North Macedonia": "North Macedonia",
    "Norway": "Norway",
    "Oman": "Oman",
    "Pakistan": "Pakistan",
    "Palau": "Palau",
    "Panama": "Panama",
    "Papua New Guinea": "Papua New Guinea",
    "Paraguay": "Paraguay",
    "Peru": "Peru",
    "Philippines": "Philippines",
    "Poland": "Poland",
    "Portugal": "Portugal",
    "Qatar": "Qatar",
    "Romania": "Romania",
    "Russia": "Russia",
    "Rwanda": "Rwanda",
    "Saint Kitts and Nevis": "Saint Kitts and Nevis",
    "Saint Lucia": "Saint Lucia",
    "Saint Vincent and the Grenadines": "Saint Vincent and the Grenadines",
    "Samoa": "Samoa",
    "San Marino": "San Marino",
    "Sao Tome and Principe": "Sao Tome and Principe",
    "Saudi Arabia": "Saudi Arabia",
    "Senegal": "Senegal",
    "Serbia": "Serbia",
    "Seychelles": "Seychelles",
    "Sierra Leone": "Sierra Leone",
    "Singapore": "Singapore",
    "Slovakia": "Slovakia",
    "Slovenia": "Slovenia",
    "Solomon Islands": "Solomon Islands",
    "Somalia": "Somalia",
    "South Africa": "South Africa",
    "South Korea": "South Korea",
    "South Sudan": "South Sudan",
    "Spain": "Spain",
    "Sri Lanka": "Sri Lanka",
    "Sudan": "Sudan",
    "Suriname": "Suriname",
    "Sweden": "Sweden",
    "Switzerland": "Switzerland",
    "Syria": "Syria",
    "Taiwan": "Taiwan",
    "Tajikistan": "Tajikistan",
    "Tanzania": "Tanzania",
    "Thailand": "Thailand",
    "Timor-Leste": "Timor-Leste",
    "Togo": "Togo",
    "Tonga": "Tonga",
    "Trinidad and Tobago": "Trinidad and Tobago",
    "Tunisia": "Tunisia",
    "Turkey": "Turkey",
    "Turkmenistan": "Turkmenistan",
    "Tuvalu": "Tuvalu",
    "Uganda": "Uganda",
    "Ukraine": "Ukraine",
    "United Arab Emirates": "United Arab Emirates",
    "United Kingdom": "United Kingdom",
    "Uruguay": "Uruguay",
    "Uzbekistan": "Uzbekistan",
    "Vanuatu": "Vanuatu",
    "Vatican City": "Vatican City",
    "Venezuela": "Venezuela",
    "Vietnam": "Vietnam",
    "Yemen": "Yemen",
    "Zambia": "Zambia",
    "Zimbabwe": "Zimbabwe"
};


// ADL Products list
export const ADLProducts = {
    SNCA10SUGARCALC: "ADL",
    BSIMPULSEC: "ADL",
    BSADAMSC: "ADL",
    LF000027C: "ADL",
    PWHYDRATIONAPC: "ADL",
    PWFLOTHRUAPC: "ADL",
    LF082705C: "ADL",
    MNFE66IRONC: "ADL",
    BSHUMIC19C: "ADL",
    BSKELP1C: "ADL",
    MNMN7PHUSIONC: "ADL",
    LF29000050MC: "ADL",
    LF070007MC: "ADL",
    SNCA9S4LIQUAGYPC: "ADL",
    MNFE4KELPIRONC: "ADL",
    SNCA1230CALC: "ADL",
    LF000029C: "ADL",
    MNMICROPACKC: "ADL",
    LF022022DKPC: "ADL",
    PWHYDRATIONACC: "ADL",
    MNSI21SILIKC: "ADL",
    SNCA11CALNITRATEC: "ADL",
    BSMOLASSESC4: "ADL",
    MNFE35HA3C: "ADL",
};


// Mapping of industry values to their display names
export const industryMapping = {
    BOTANICAL: "Botanical Garden",
    DISTRIB: "Product Distributor",
    EMPLOYEE: "PFC-EMPLOYEE",
    FARM: "Agriculture - Farm",
    GOLF: "Golf Course",
    GOVERNMENT: "Government Entity",
    INDUSTRIAL: "Industrial",
    LAWNTREE: "Lawn Care & Tree Care",
    MRSALE: "One Time Purchase Expected",
    MUNICIPAL: "Municipal/ School",
    "Non-Profit": "Non-Profit Account",
    OTHER: "Other",
    SPORTFLD: "Sportsfield"
};


export const salesCodeMapping = {
    CF: "Phillip String",
    CN: "Jason Dowgiewicz",
    DH: "Direct House Account",
    DS: "Jennifer Porter",
    FL: "James Rattigan",
    FN: "Christopher Allen",
    FS: "Jordan Nemitz",
    FW: "Cory Agar",
    GC: "Tom Weinert",
    GP: "Grant Platz",
    JC: "Tom Pepe",
    JK: "Jeffery Knapp",
    JM: "Joseph McKenney",
    MF: "Michael Giovanelli",
    NJ: "Richard Neufeld",
    PG: "Michael Janzer",
    RS: "Richard Sweeney",
    SJ: "David St. John",
    WI: "David Conrad",
    MW: "Michael Weinert",
    TP: "Ted Platz",
    SW: "Spencer Wondolowski",
    JF: "Jonathan Finney",
    BC: "Ben Casella",
};

export const salesIDMapping = {
    'XvaoHwqZd4dyiRbYbmhfZljLiVR2': "Phillip String",
    'N9yGo63pbQcM8Wc86dkN2JP3kMf2': "Jason Dowgiewicz",
    DH: "Direct House Account",
    DS: "Gregory Moore",
    '2Wr5LgVwwSdNz1mDvZ8YnmBwfqi2': "James Rattigan",
    'yzFk02o5sSVlW25F7yhj4s6QPfq2': "Christopher Allen",
    '6Ip2wUCfYUW1cg2ZPPzWi3zAaa03': "Jordan Nemitz",
    'GrvpgLhZS6e2FvB1ZuQkU5jtVVN2': "Cory Agar",
    '8lM2h64LWVNEWaInvO0ERxn3fR52': "Tom Weinert",
    '9nqTaGIMElcjmwQPtV3YncR0hzM2': "Grant Platz",
    'mRHfuOLzSGZMnESXxAXJPvXwNwN2': "Tom Pepe",
    JK: "Jeffery Knapp",
    'NxMfEJYkE0Xe3WowJ4Vy8ckHrvk2': "Joseph McKenney",
    '2nlZTzSX1AWmTG4h4gMTGZKPX7U2': "Michael Giovanelli",
    'QCKGlgnI5wNpC0onHObXU5Bbx812': "Richard Neufeld",
    'XnIlidyBGMUq26u8fFwi90gGFpi1': "Michael Janzer",
    'fcE1oOBWCYRjXOteiQ7LZQqP1vi2': "Richard Sweeney",
    '1pK7GhP9aHRZwCwHskVhzvs5nE93': "David St. John",
    'BEhGYVkkWDP67pfKrJ7x9VYkPox1': "David Conrad",
    'GoZrIo036PN66X1xtQ97x2vW8VE3': "Michael Weinert",
    'XdD59xgiUEf6q8uJ3Gh7sxXdv2r2': "Ted Platz",
    'gizjXN97yfbwLeoiNWSNQFIHoLR2': "Spencer Wondolowski",
    'CHUoPLmYuaaqHFKHDY3BVMAFfzh2': "Ben Casella",
};


export const warehouseOptions = [
    {value: "MAIN", label: "Plant Food Company - NJ", regions: ["NE", "SE", "AG", "DIST1", "DIST2"]},
    {value: "ADL", label: "ADL, Florida 3PL", regions: ["SE"]},
    {value: "CFNJ", label: "Phil String, Storage", regions: ["AG"]},
    {value: "CNCT", label: "Jason Dowgiewicz, Storage", regions: ["NE"]},
    {value: "FLFL", label: "Jim Rattigan, Storage", regions: ["SE"]},
    {value: "FNFL", label: "Christopher Allen, Storage", regions: ["SE"]},
    {value: "FSFL", label: "Jordan Nemitz, Storage", regions: ["SE"]},
    {value: "JMFL", label: "Joe Mckenney, Storage", regions: ["SE"]},
    {value: "FWFL", label: "Cory Agar, Storage", regions: ["SE"]},
    {value: "GCPA", label: "Tom Weinert, Storage", regions: ["NE"]},
    {value: "JCNJ", label: "Tom Pepe, Storage", regions: ["NE", "AG"]},
    {value: "MFNJ", label: "Mike Giovanelli, Storage", regions: ["AG"]},
    {value: "NJNJ", label: "Richard Neufeld, Storage", regions: ["NE"]},
    {value: "PFSY", label: "PLANT FOOD SYSTEMS, FL", regions: ["SE"]},
    {value: "PGPA", label: "Mike Janzer, Storage", regions: ["NE"]},
    {value: "RSNJ", label: "Richard Sweeney, Storage", regions: ["NE"]},
    {value: "SJNJ", label: "David St. John, Storage", regions: ["AG"]},
    {value: "WINY", label: "David Conrad, Storage", regions: ["NE"]},
    {value: "MWCT", label: "Michael Weinert, Storage", regions: ["NE"]},
    {value: "BCNJ", label: "Ben Casella, Storage", regions: ["NE"]},
];


export const soilTestMapping = {
    IWTS: "Irrigation Water Test Suitability Package",
    PSF: "Physical with Sand Fraction",
    CTP: "Complete Tissue Package",
    SPP: "Saturated Paste Package",
    SSP: "Standard Soil Package",
    CSP: "Complete Soil Package",
};

export function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export const getQuarterString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-based: Jan = 0, Dec = 11

  const quarter = Math.floor(month / 3) + 1; // divide month by 3 and add 1
  return `Q${quarter} ${year}`;
}

export const getYearString = (date = new Date()) => {
  const year = date.getFullYear();

  return `${year}`;
}

export function formatUnixTimestampToReadableString(unixTimestampStr) {
  if (!unixTimestampStr) return "No update time available";

  const date = new Date(Number(unixTimestampStr));
  if (isNaN(date)) return "Invalid timestamp";

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = date.getDate();
  const daySuffix = getDaySuffix(day);
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;

  return `${month} ${day}${daySuffix} ${year} at ${hours}:${minutes}${ampm}`;
}

function getDaySuffix(day) {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
}


export const priceTypeColor = {
    NE: "bg-green-600/15 text-green-600",
    SE: "bg-blue-600/15 text-blue-600",
    AG: "bg-orange-600/15 text-orange-600",
    DIST1: "bg-green-600/15 text-green-600",
    DIST2: "bg-green-600/15 text-green-600",
}

export function getCardinalDirection(deg) {
    if (deg === null || deg === undefined || isNaN(deg)) return '--';

    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(deg / 45) % 8;
    return directions[index];
}


export function formatDateStampShort(input) {
  if (!input) return "";

  let date;

  // Support Firestore timestamp, Date, or number/string
  if (input?.seconds) {
    date = new Date(input.seconds * 1000);
  } else if (input instanceof Date) {
    date = input;
  } else {
    date = new Date(input);
  }

  const month = date.getMonth() + 1;   // no leading zero
  const day = date.getDate();          // no leading zero
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0"); // keep leading zero

  const ampm = hours >= 12 ? "pm" : "am";

  hours = hours % 12;
  hours = hours === 0 ? 12 : hours; // handle midnight/noon

  return `${month}/${day}/${year} | ${hours}:${minutes} ${ampm}`;
}
